import { gql, Apollo } from "apollo-angular";
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject,
} from "@angular/core";

import { InterfaceSearchItem, Query } from "../../../../../codegen/out/types";
import { FormControl } from "@angular/forms";
import { COMMA, ENTER } from "@angular/cdk/keycodes";
import { Observable, Subscription } from "rxjs";
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
} from "@angular/material/autocomplete";
import { map, startWith } from "rxjs/operators";
import { MatChipInputEvent } from "@angular/material/chips";
import { unsubscribe } from "src/app/core/subscription.helper";

const searchInterfacesQuery = gql`
  query {
    searchInterfaces {
      interfaces {
        name
        id
      }
    }
  }
`;

@Component({
  selector: "app-interface-select",
  templateUrl: "./interface-select.component.html",
  styleUrls: ["./interface-select.component.scss"],
  standalone: false,
})
export class InterfaceSelectComponent implements OnInit, OnDestroy {
  private apollo = inject(Apollo);

  subscriptions: { [key: string]: Subscription } = {};
  removable = true;
  interfaces: InterfaceSearchItem[];
  filteredInterfaces: Observable<InterfaceSearchItem[]>;
  interfacesQuery: any;
  interfaceCtrl = new FormControl();
  separatorKeysCodes: number[] = [ENTER, COMMA];
  selectedInterfaces: InterfaceSearchItem[] = [];

  @ViewChild("interfaceInput")
  interfaceInput: ElementRef<HTMLInputElement>;
  @ViewChild("auto") matAutocomplete: MatAutocomplete;

  @Input() selectedInterfaceIds = [];
  @Output() updated = new EventEmitter<number[]>();

  ngOnInit() {
    this.interfacesQuery = this.apollo.query({
      query: searchInterfacesQuery,
    });

    this.subscriptions.interfaces = this.interfacesQuery.subscribe(
      (result: { data: Query }) => {
        this.interfaces = this.parseQueryResult(result.data);
        this.filteredInterfaces = this.interfaceCtrl.valueChanges.pipe(
          startWith(""),
          map((value) => (typeof value === "string" ? value : value.name)),
          map((name) => (name ? this.filter(name) : this.interfaces.slice()))
        );
        this.selectedInterfaces = this.interfaces.filter((interfaceItem) => {
          return this.selectedInterfaceIds.indexOf(interfaceItem.id) !== -1;
        });
      }
    );
  }

  ngOnDestroy() {
    unsubscribe(this.subscriptions);
  }

  add(event: MatChipInputEvent): void {
    const input = event.chipInput.inputElement;
    const value = event.value;
    const selected = this.interfaces.find(
      (interfaceItem) => interfaceItem.name === value
    );
    if (selected) {
      this.selectedInterfaces.push(selected);
    }

    // Reset the input value
    if (input) {
      input.value = "";
    }
    this.interfaceCtrl.setValue("");
  }

  remove(interfaceInput: InterfaceSearchItem): void {
    const index = this.selectedInterfaces.findIndex(
      (interfaceItem) => interfaceItem.id === interfaceInput.id
    );
    this.selectedInterfaces.splice(index, 1);
    this.updated.emit(
      this.selectedInterfaces.map((interfaceItem) =>
        Number.parseInt(interfaceItem.id)
      )
    );
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    this.selectedInterfaces.push(event.option.value);
    this.interfaceInput.nativeElement.value = "";
    this.interfaceCtrl.setValue("");
    this.updated.emit(
      this.selectedInterfaces.map((interfaceItem) =>
        Number.parseInt(interfaceItem.id)
      )
    );
  }

  private filter(value: string): InterfaceSearchItem[] {
    const filterValue = value.toLowerCase();

    return this.interfaces.filter(
      (interfaceItem) =>
        interfaceItem.name.toLowerCase().indexOf(filterValue) === 0
    );
  }

  private parseQueryResult(data: Query): InterfaceSearchItem[] {
    if (data == null) {
      return [
        {
          name: "...",
          id: "-1",
        } as InterfaceSearchItem,
      ];
    }

    return data.searchInterfaces.interfaces;
  }
}
